import { css, html, LitElement } from 'lit';
import type { BracketSize, CountdownTimer, GameMode, LobbyPlayer } from '@/shared/messages.ts';

export class BizLobby extends LitElement {
  static properties = {
    roomCode: { type: String, attribute: 'room-code' },
    players: { attribute: false },
    hostUserId: { type: String, attribute: 'host-user-id' },
    selfUserId: { type: String, attribute: 'self-user-id' },
    size: { type: Number },
    mode: { type: String },
    startingTimer: { attribute: false },
    _remaining: { state: true },
  };

  declare roomCode: string;
  declare players: LobbyPlayer[];
  declare hostUserId: string;
  declare selfUserId: string;
  declare size: BracketSize;
  declare mode: GameMode;
  declare startingTimer: CountdownTimer | null;
  declare _remaining: number;

  #tickHandle: number | null = null;

  constructor() {
    super();
    this.roomCode = '';
    this.players = [];
    this.hostUserId = '';
    this.selfUserId = '';
    this.size = 8;
    this.mode = 'basic';
    this.startingTimer = null;
    this._remaining = 0;
  }

  static styles = css`
    :host {
      display: block;
      max-width: 36rem;
      margin: 2rem auto;
      padding: 0 1rem;
      position: relative;
    }
    .leave {
      position: absolute;
      top: 0;
      left: 1rem;
    }
    h1 {
      margin: 0 0 0.25rem 0;
      letter-spacing: -0.01em;
    }
    .code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 1.2rem;
      color: var(--fg-dim, #a0a4ad);
      margin-bottom: 1.5rem;
    }
    .code strong {
      color: var(--fg, #ececec);
      letter-spacing: 0.2em;
      font-size: 1.6rem;
    }
    section {
      background: var(--bg-card, #1f2128);
      border: 1px solid var(--border, #2c2e36);
      border-radius: 8px;
      padding: 1rem 1.25rem;
      margin-bottom: 1rem;
    }
    section h2 {
      margin: 0 0 0.75rem 0;
      font-size: 1rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--fg-dim, #a0a4ad);
    }
    .players {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .players li {
      display: flex;
      align-items: center;
      gap: 0.6rem;
    }
    .swatch {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      flex: 0 0 auto;
    }
    .host-tag {
      font-size: 0.7rem;
      background: var(--accent, #5fbf73);
      color: #0a0b0d;
      padding: 0.1em 0.5em;
      border-radius: 999px;
      font-weight: 700;
      letter-spacing: 0.05em;
    }
    .you-tag {
      font-size: 0.7rem;
      color: var(--fg-dim, #a0a4ad);
    }
    .settings-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6rem 1.2rem;
      margin-bottom: 0.6rem;
    }
    .settings-row:last-child {
      margin-bottom: 0;
    }
    .settings-row label {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      color: var(--fg-dim, #a0a4ad);
    }
    .settings-row label.selected {
      color: var(--fg, #ececec);
    }
    .actions {
      display: flex;
      gap: 0.5rem;
      margin-top: 1rem;
      align-items: center;
    }
    .countdown {
      font-size: 1.2rem;
      font-variant-numeric: tabular-nums;
    }
    .hint {
      color: var(--fg-dim, #a0a4ad);
      font-size: 0.9rem;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.#startTicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTicker();
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('startingTimer')) {
      this.#stopTicker();
      this.#startTicker();
    }
  }

  render() {
    const isHost = this.hostUserId === this.selfUserId;
    const playerCount = this.players.length;
    const canStart = isHost && playerCount >= 3 && !this.startingTimer;
    const sizeOptions: BracketSize[] = [4, 8, 16];
    const modeOptions: GameMode[] = ['basic', 'blind', 'multi-blind'];

    return html`
      <link rel="stylesheet" href="/static/style.css" />
      <button class="leave secondary" @click="${() => this.#onLeave()}">
        Leave
      </button>
      <h1>Lobby</h1>
      <div class="code">
        Room code: <strong>${this.roomCode}</strong>
      </div>

      <section>
        <h2>Players (${playerCount})</h2>
        <ul class="players">
          ${this.players.map(
            (p) =>
              html`
                <li>
                  <span class="swatch" style="${`background: ${p.color}`}"></span>
                  <span>${p.name}</span>
                  ${p.userId === this.hostUserId
                    ? html`
                      <span class="host-tag">host</span>
                    `
                    : null} ${p.userId === this.selfUserId
                    ? html`
                      <span class="you-tag">(you)</span>
                    `
                    : null}
                </li>
              `,
          )}
        </ul>
        ${playerCount < 3
          ? html`
            <p class="hint">Need at least 3 players to start.</p>
          `
          : null}
      </section>

      <section>
        <h2>Settings</h2>
        <div class="settings-row">
          <span>Size:</span>
          ${sizeOptions.map(
            (s) =>
              html`
                <label class="${this.size === s ? 'selected' : ''}">
                  <input
                    type="radio"
                    name="size"
                    ?checked="${this.size === s}"
                    ?disabled="${!isHost || !!this.startingTimer || !this.#sizeAllowed(s)}"
                    @change="${() => this.#onSize(s)}"
                  />
                  ${s}
                </label>
              `,
          )}
        </div>
        <div class="settings-row">
          <span>Mode:</span>
          ${modeOptions.map(
            (m) =>
              html`
                <label class="${this.mode === m ? 'selected' : ''}">
                  <input
                    type="radio"
                    name="mode"
                    ?checked="${this.mode === m}"
                    ?disabled="${!isHost || !!this.startingTimer}"
                    @change="${() => this.#onMode(m)}"
                  />
                  ${m}
                </label>
              `,
          )}
        </div>
      </section>

      <div class="actions">
        ${this.startingTimer
          ? html`
            <span class="countdown">Starting in ${this._remaining}s&hellip;</span>
            ${isHost
              ? html`
                <button class="danger" @click="${() => this.#onCancel()}">
                  Cancel
                </button>
              `
              : null}
          `
          : isHost
          ? html`
            <button ?disabled="${!canStart}" @click="${() => this.#onStart()}">
              Start game
            </button>
          `
          : html`
            <span class="hint">Waiting for the host to start the game&hellip;</span>
          `}
      </div>
    `;
  }

  #sizeAllowed(size: BracketSize): boolean {
    const count = this.players.length;
    if (size === 4) return count <= 4;
    if (size === 16) return count >= 7;
    return true;
  }

  #onSize(size: BracketSize) {
    this.dispatchEvent(
      new CustomEvent('biz-update-settings', {
        detail: { size, mode: this.mode },
        bubbles: true,
        composed: true,
      }),
    );
  }
  #onMode(mode: GameMode) {
    this.dispatchEvent(
      new CustomEvent('biz-update-settings', {
        detail: { size: this.size, mode },
        bubbles: true,
        composed: true,
      }),
    );
  }
  #onStart() {
    this.dispatchEvent(
      new CustomEvent('biz-start-game', { bubbles: true, composed: true }),
    );
  }
  #onCancel() {
    this.dispatchEvent(
      new CustomEvent('biz-cancel-start', { bubbles: true, composed: true }),
    );
  }
  #onLeave() {
    this.dispatchEvent(
      new CustomEvent('biz-leave', { bubbles: true, composed: true }),
    );
  }

  #startTicker() {
    if (!this.startingTimer) return;

    // Anchor against the local monotonic clock at the moment we received the
    // timer message — avoids cross-device clock skew. The server still owns
    // when the countdown actually expires; this is purely a UI estimate.
    const anchor = performance.now();
    const totalMs = this.startingTimer.ms;
    const tick = () => {
      if (!this.startingTimer) {
        this.#stopTicker();
        return;
      }
      if (this.startingTimer.paused) {
        this._remaining = Math.max(0, Math.ceil(totalMs / 1000));
        return;
      }
      const elapsedMs = performance.now() - anchor;
      this._remaining = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000));
    };
    tick();
    this.#tickHandle = setInterval(tick, 250) as unknown as number;
  }

  #stopTicker() {
    if (this.#tickHandle !== null) {
      clearInterval(this.#tickHandle);
      this.#tickHandle = null;
    }
  }
}

customElements.define('biz-lobby', BizLobby);
